import { computed, inject, Injectable, signal } from '@angular/core';
import { CartItem, PedidoDetalle, CreatePedidoDto } from '../models/pedido.models';
import { PedidoApiService } from '../services/pedido.service';
import { ToastService } from '../../../shared/components/toast/toast.service';
import { catchError, finalize, of, tap } from 'rxjs';

export interface CartState {
  items: CartItem[];
  pedidoActivo: PedidoDetalle | null;
  loading: boolean;
  error: string | null;
}

const INITIAL_STATE: CartState = {
  items: [],
  pedidoActivo: null,
  loading: false,
  error: null,
};

@Injectable({
  providedIn: 'root',
})
export class CartStateService {
  private api = inject(PedidoApiService);
  private toast = inject(ToastService);

  // Estado principal
  private state = signal<CartState>(INITIAL_STATE);

  // Selectores
  items = computed(() => this.state().items);
  pedidoActivo = computed(() => this.state().pedidoActivo);
  loading = computed(() => this.state().loading);
  error = computed(() => this.state().error);

  // Computed: total de items
  totalItems = computed(() =>
    this.state().items.reduce((sum, item) => sum + item.cantidad, 0)
  );

  // Computed: total de materiales
  totalMateriales = computed(() =>
    this.state().items.filter((i) => i.tipo === 'MATERIAL').length
  );

  // Computed: total de equipos
  totalEquipos = computed(() =>
    this.state().items.filter((i) => i.tipo === 'EQUIPO').length
  );

    

  // Computed: carrito vacío
  isEmpty = computed(() => this.state().items.length === 0);

  // True cuando el carrito tiene ítems de ambos sectores (PLANTA y DEPOSITO_CENTRAL)
  hasMixedSectors = computed(() => {
    const sectors = new Set(
      this.state().items
        .map(i => i.ubicacion_sector)
        .filter((s): s is string => s != null)
    );
    return sectors.size > 1;
  });

  // Computed: tiene pedido activo
  hasPedidoActivo = computed(() => this.state().pedidoActivo !== null);

  // ==================== ACCIONES LOCALES ====================

  /**
   * Agrega un material al carrito local
   */
  addMaterial(material: {
    id: number;
    nombre: string;
    codigo?: string;
    precio_unitario?: number;
    stock_actual?: number;
    imagen?: string;
    tipo_presentacion?: string;
    ubicacion_sector?: string | null;
  }, cantidad: number = 1) {
    const items = this.state().items;
    const existingIndex = items.findIndex(
      (i) => i.tipo === 'MATERIAL' && i.recurso_id === material.id
    );

    if (existingIndex >= 0) {
      // Actualizar cantidad
      const updatedItems = [...items];
      updatedItems[existingIndex] = {
        ...updatedItems[existingIndex],
        cantidad: updatedItems[existingIndex].cantidad + cantidad,
      };
      this.state.update((s) => ({ ...s, items: updatedItems }));
    } else {
      // Agregar nuevo
      const newItem: CartItem = {
        tipo: 'MATERIAL',
        recurso_id: material.id,
        nombre: material.nombre,
        codigo: material.codigo || null,
        cantidad,
        precio_unitario: material.precio_unitario ?? null,
        stock_actual: material.stock_actual ?? null,
        imagen: material.imagen || null,
        tipo_presentacion: material.tipo_presentacion || null,
        ubicacion_sector: material.ubicacion_sector ?? null,
      };
      this.state.update((s) => ({ ...s, items: [...s.items, newItem] }));
    }

    this.toast.show(`${material.nombre} agregado al carrito`, 'success');
  }

  /**
   * Agrega un equipo al carrito local
   */
  addEquipo(equipo: {
    id: number;
    nombre: string;
    codigo_patrimonio?: string;
    precio_unitario?: number;
    imagen?: string;
    marca?: string;
    modelo?: string;
    ubicacion_sector?: string | null;
  }, estadoDestinoId?: number) {
    const items = this.state().items;
    const exists = items.some(
      (i) => i.tipo === 'EQUIPO' && i.recurso_id === equipo.id
    );

    if (exists) {
      this.toast.show('Este equipo ya está en el carrito', 'info');
      return;
    }

    const newItem: CartItem = {
      tipo: 'EQUIPO',
      recurso_id: equipo.id,
      nombre: equipo.nombre,
      codigo: equipo.codigo_patrimonio || null,
      cantidad: 1,
      precio_unitario: equipo.precio_unitario ?? null,
      stock_actual: null,
      imagen: equipo.imagen || null,
      marca: equipo.marca || null,
      modelo: equipo.modelo || null,
      estado_destino_id: estadoDestinoId,
      ubicacion_sector: equipo.ubicacion_sector ?? null,
    };

    this.state.update((s) => ({ ...s, items: [...s.items, newItem] }));
    this.toast.show(`${equipo.nombre} agregado al carrito`, 'success');
  }

  /**
   * Actualiza la cantidad de un item (solo materiales)
   */
  updateCantidad(tipo: 'MATERIAL' | 'EQUIPO', recursoId: number, cantidad: number) {
    if (tipo === 'EQUIPO') return; // Equipos siempre cantidad 1

    const items = this.state().items;
    const updatedItems = items.map((item) => {
      if (item.tipo === tipo && item.recurso_id === recursoId) {
        return { ...item, cantidad: Math.max(1, cantidad) };
      }
      return item;
    });

    this.state.update((s) => ({ ...s, items: updatedItems }));
  }

  /**
   * Elimina un item del carrito local
   */
  removeItem(tipo: 'MATERIAL' | 'EQUIPO', recursoId: number) {
    const items = this.state().items.filter(
      (i) => !(i.tipo === tipo && i.recurso_id === recursoId)
    );
    this.state.update((s) => ({ ...s, items }));
  }

  /**
   * Limpia el carrito local
   */
  clearCart() {
    this.state.update((s) => ({ ...s, items: [], pedidoActivo: null }));
  }

  // ==================== ACCIONES CON API ====================

  /**
   * Crea un pedido con los items del carrito
   */
  crearPedido(data: CreatePedidoDto) {
    this.state.update((s) => ({ ...s, loading: true, error: null }));

    return this.api.createPedido(data).pipe(
      tap((pedido) => {
        this.state.update((s) => ({ ...s, pedidoActivo: pedido }));
        // Sincronizar items del carrito con el pedido
        this.sincronizarItemsConPedido(pedido.id);
      }),
      catchError((err) => {
        this.state.update((s) => ({ ...s, error: err.message }));
        this.toast.show(err.message, 'error');
        return of(null);
      }),
      finalize(() => this.state.update((s) => ({ ...s, loading: false })))
    );
  }

  /**
   * Sincroniza los items del carrito local con el pedido en el servidor
   */
  private sincronizarItemsConPedido(pedidoId: number) {
    const items = this.state().items;

    // Agregar materiales
    const materiales = items.filter((i) => i.tipo === 'MATERIAL');
    materiales.forEach((item) => {
      this.api
        .addMaterial(pedidoId, {
          material_id: item.recurso_id,
          cantidad: item.cantidad,
          precio_unitario: item.precio_unitario ?? undefined,
        })
        .subscribe({
          next: (pedido) => {
            this.state.update((s) => ({ ...s, pedidoActivo: pedido }));
          },
          error: (err) => {
            this.toast.show(`Error agregando ${item.nombre}: ${err.message}`, 'error');
          },
        });
    });

    // Agregar equipos
    const equipos = items.filter((i) => i.tipo === 'EQUIPO');
    equipos.forEach((item) => {
      this.api
        .addEquipo(pedidoId, {
          equipo_id: item.recurso_id,
          estado_destino_id: item.estado_destino_id,
        })
        .subscribe({
          next: (pedido) => {
            this.state.update((s) => ({ ...s, pedidoActivo: pedido }));
          },
          error: (err) => {
            this.toast.show(`Error agregando ${item.nombre}: ${err.message}`, 'error');
          },
        });
    });
  }

  /**
   * Carga un pedido existente para continuar editando
   */
  cargarPedido(id: number) {
    this.state.update((s) => ({ ...s, loading: true, error: null }));

    return this.api.getPedidoById(id).pipe(
      tap((pedido) => {
        // Convertir items del pedido a items del carrito
        const items: CartItem[] = pedido.items.map((item) => ({
          tipo: item.tipo_item,
          recurso_id: item.recurso_id,
          nombre: item.nombre,
          codigo: item.codigo,
          cantidad: item.cantidad,
          precio_unitario: item.precio_unitario,
          stock_actual: item.stock_actual,
          imagen: item.imagen,
          tipo_presentacion: item.tipo_presentacion,
        }));

        this.state.update((s) => ({
          ...s,
          items,
          pedidoActivo: pedido,
        }));
      }),
      catchError((err) => {
        this.state.update((s) => ({ ...s, error: err.message }));
        this.toast.show(err.message, 'error');
        return of(null);
      }),
      finalize(() => this.state.update((s) => ({ ...s, loading: false })))
    );
  }

  /**
   * Entrega el pedido activo
   */
  entregarPedido(entregadoPor?: string) {
    const pedido = this.state().pedidoActivo;
    if (!pedido) {
      this.toast.show('No hay pedido activo', 'error');
      return of(null);
    }

    this.state.update((s) => ({ ...s, loading: true, error: null }));

    return this.api.entregarPedido(pedido.id, entregadoPor).pipe(
      tap((pedidoEntregado) => {
        this.state.update((s) => ({
          ...s,
          pedidoActivo: pedidoEntregado,
          items: [], // Limpiar carrito después de entregar
        }));
        this.toast.show('Pedido entregado exitosamente', 'success');
      }),
      catchError((err) => {
        this.state.update((s) => ({ ...s, error: err.message }));
        this.toast.show(err.message, 'error');
        return of(null);
      }),
      finalize(() => this.state.update((s) => ({ ...s, loading: false })))
    );
  }

  /**
   * Cancela el pedido activo
   */
  cancelarPedido() {
    const pedido = this.state().pedidoActivo;
    if (!pedido) return of(null);

    this.state.update((s) => ({ ...s, loading: true }));

    return this.api.cancelarPedido(pedido.id).pipe(
      tap(() => {
        this.clearCart();
        this.toast.show('Pedido cancelado', 'info');
      }),
      catchError((err) => {
        this.toast.show(err.message, 'error');
        return of(null);
      }),
      finalize(() => this.state.update((s) => ({ ...s, loading: false })))
    );
  }
}
